import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { TrendingUp, Package, Clock, CheckCircle2, DollarSign, ArrowRight } from "lucide-react";

const STATUS_BADGES = {
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
const STATUS_LABELS = { pending_payment: "Pending", in_progress: "In Progress", completed: "Completed" };

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setRecent(r.data.slice(0, 10))).catch(() => {});
  }, []);

  const maxRevenue = Math.max(1, ...(stats?.chart?.map((d) => d.revenue) || [0]));

  return (
    <div data-testid="admin-overview-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#2563eb] font-semibold">Dashboard</div>
        <h1 className="mt-1 font-display font-bold text-3xl text-[#0f1b2d]">Overview</h1>
        <p className="mt-1 text-[#475569]">Real-time snapshot of all SchengenFile orders.</p>
      </div>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard icon={Package} label="Paid Orders" value={stats?.total ?? "—"} color="bg-slate-100 text-slate-700" data-testid="stat-total" />
        <StatCard icon={Clock} label="Unpaid" value={stats?.unpaid ?? "—"} color="bg-amber-100 text-amber-700" data-testid="stat-unpaid" />
        <StatCard icon={TrendingUp} label="In Progress" value={stats?.in_progress ?? "—"} color="bg-blue-100 text-blue-700" data-testid="stat-in_progress" />
        <StatCard icon={CheckCircle2} label="Completed" value={stats?.completed ?? "—"} color="bg-emerald-100 text-emerald-700" data-testid="stat-completed" />
        <StatCard icon={DollarSign} label="Revenue" value={stats ? `$${Math.round(stats.revenue)}` : "—"} color="bg-[#dbeafe] text-[#2563eb]" data-testid="stat-revenue" />
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2 bg-white border border-[#e2e8f0] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-[#0f1b2d]">Revenue · last 7 days</h3>
            <span className="text-xs text-[#64748b]">Paid orders only</span>
          </div>
          <div className="mt-6 flex items-end gap-3 h-48">
            {(stats?.chart || []).map((d) => {
              const h = (d.revenue / maxRevenue) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-2" data-testid={`chart-bar-${d.date}`}>
                  <div className="w-full flex-1 flex items-end">
                    <div
                      className="w-full bg-gradient-to-t from-[#2563eb] to-[#60a5fa] rounded-t-md transition-all"
                      style={{ height: `${Math.max(2, h)}%` }}
                      title={`$${d.revenue}`}
                    />
                  </div>
                  <div className="text-[10px] text-[#64748b]">{d.date.slice(5)}</div>
                  <div className="text-xs font-semibold text-[#0f1b2d]">${Math.round(d.revenue)}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent orders */}
        <div className="bg-white border border-[#e2e8f0] rounded-lg p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-[#0f1b2d]">Recent orders</h3>
            <Link to="/admin/orders" className="text-xs text-[#2563eb] font-semibold inline-flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="mt-4 space-y-3">
            {recent.length === 0 ? (
              <div className="text-sm text-[#475569] py-6 text-center">No recent orders.</div>
            ) : (
              recent.map((o) => (
                <Link to="/admin/orders" key={o.id} className="block p-3 rounded-md border border-[#e2e8f0] hover:border-[#2563eb] transition">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0f1b2d] truncate">{o.full_name}</div>
                      <div className="text-xs text-[#64748b]">{o.destination_country} · {o.package_name}</div>
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGES[o.status]}`}>
                      {STATUS_LABELS[o.status]}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, ...rest }) {
  return (
    <div className="bg-white border border-[#e2e8f0] rounded-lg p-5" {...rest}>
      <div className={`w-9 h-9 rounded-md grid place-items-center ${color}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-4 text-xs text-[#64748b] uppercase tracking-wider">{label}</div>
      <div className="mt-1 font-display font-black text-2xl text-[#0f1b2d]">{value}</div>
    </div>
  );
}
